import { Package, ShoppingBag, Tag, Users, type LucideIcon } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Skeleton } from '@/components/ui/skeleton'

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'

import { filterNav, sidebarRoutes, type NavItem } from './navIndex'
import { useCommandSearch, type SearchResultItem } from './useCommandSearch'

interface Props {
  open: boolean
  onClose: () => void
}

function ResultSkeleton() {
  return (
    <div className="space-y-1 px-2 py-1" aria-hidden="true">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-md px-2 py-2">
          <Skeleton className="size-4 shrink-0 rounded" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3.5 w-2/3 rounded" />
            <Skeleton className="h-3 w-1/3 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

const GROUPS: { key: keyof ReturnType<typeof useCommandSearch>['results'], heading: string, icon: LucideIcon }[] = [
  { key: 'orders', heading: 'Orders', icon: ShoppingBag },
  { key: 'products', heading: 'Products', icon: Package },
  { key: 'customers', heading: 'Customers', icon: Users },
  { key: 'coupons', heading: 'Coupons', icon: Tag },
]

const QUICK: { label: string, route: string, icon: LucideIcon }[] = [
  { label: 'Orders', route: '/orders', icon: ShoppingBag },
  { label: 'Products', route: '/products', icon: Package },
  { label: 'Customers', route: '/customers', icon: Users },
]

/**
 * ⌘K / Ctrl K palette: pages (instant, only those the sidebar shows) and live search over orders, products,
 * customers and coupons. Results come from the server, so cmdk's own filtering is off.
 */
export function CommandPalette({ open, onClose }: Props) {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { getSidebarItems } = useFeatureFlags()
  const visible = useMemo(() => sidebarRoutes(getSidebarItems()), [getSidebarItems])
  const { results, loading, failed, tooShort } = useCommandSearch(query)
  const navMatches = filterNav(query, visible)
  const quick = QUICK.filter((q) => visible.has(q.route))

  const close = () => {
    onClose()
    setQuery('')
  }
  const go = (route: string) => {
    navigate(route)
    close()
  }

  const groups = GROUPS.filter((g) => results[g.key].length > 0)
  const hasResults = navMatches.length > 0 || groups.length > 0
  const typed = query.trim().length > 0

  return (
    <CommandDialog open={open} onOpenChange={(v) => { if (!v) close() }} title="Search" shouldFilter={false}>
      <CommandInput
        placeholder="Search orders, products, customers, pages…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[480px]">
        {typed && !loading && !hasResults && (
          <CommandEmpty>
            {tooShort ? 'Type at least 2 characters to search records.' : `No results for “${query.trim()}”`}
          </CommandEmpty>
        )}

        {navMatches.length > 0 && (
          <CommandGroup heading="Pages">
            {navMatches.map((item: NavItem) => (
              <CommandItem key={item.route} value={`nav:${item.route}`} onSelect={() => go(item.route)}>
                <item.icon className="mr-2 size-4 shrink-0 text-muted-foreground" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {loading && !groups.length && (
          <>
            {navMatches.length > 0 && <CommandSeparator />}
            <ResultSkeleton />
          </>
        )}

        {groups.map(({ key, heading, icon: Icon }) => (
          <Fragment key={key}>
            <CommandSeparator />
            <CommandGroup heading={heading}>
              {results[key].map((item: SearchResultItem) => (
                <CommandItem key={item.id} value={`${key}:${item.id}`} onSelect={() => go(item.route)}>
                  <Icon className="mr-2 size-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">
                    <span className="font-medium">{item.label}</span>
                    {item.sub && <span className="ml-2 text-caption text-muted-foreground">{item.sub}</span>}
                  </span>
                  {item.badge && (
                    <Badge variant="outline" className="ml-2 shrink-0 text-caption capitalize">
                      {item.badge.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </Fragment>
        ))}

        {failed && !loading && (
          <p className="px-4 py-2 text-caption text-muted-foreground" role="status">
            Some records could not be searched. Try again in a moment.
          </p>
        )}

        {!typed && quick.length > 0 && (
          <CommandGroup heading="Quick actions">
            {quick.map(({ label, route, icon: Icon }) => (
              <CommandItem key={route} value={`quick:${route}`} onSelect={() => go(route)}>
                <Icon className="mr-2 size-4 shrink-0 text-muted-foreground" />
                {label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      <div className="flex items-center gap-4 border-t px-3 py-2 text-caption text-muted-foreground">
        <span><kbd className="rounded bg-muted px-1 font-mono">↑↓</kbd> navigate</span>
        <span><kbd className="rounded bg-muted px-1 font-mono">↵</kbd> open</span>
        <span><kbd className="rounded bg-muted px-1 font-mono">Esc</kbd> close</span>
      </div>
    </CommandDialog>
  )
}
